import React, { useCallback, useState } from 'react';
import ImageViewer from 'react-simple-image-viewer';

import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation, Pagination } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import './Item.scss';

import { IProject } from './App';

SwiperCore.use([Navigation, Pagination]);

interface ComponentProps {
  data: IProject;
}

const Item: React.FC<ComponentProps> = props => {
  const data = props.data;

  const [currentImage, setCurrentImage] = useState(0);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [viewerImages, setViewerImages] = useState<Array<string>>([]);

  const openImageViewer = useCallback((index: number, images: Array<string>) => {
    setViewerImages(images);
    setCurrentImage(index);
    setIsViewerOpen(true);
  }, []);

  const closeImageViewer = () => {
    setCurrentImage(0);
    setIsViewerOpen(false);
    setViewerImages([]);
  };

  const isMajorSection = data.title === 'INTRODUCTION' || data.title === 'PROFILE' || data.title === 'WORK EXPERIENCE' || data.title === 'PERSONAL PROJECTS';

  return (
    <div className={`item-container ${isMajorSection ? 'major-section' : 'sub-section'}`}>
      <div className={`item-title-container ${isMajorSection ? 'major' : 'sub'}`}>
        <div className={`item-title-dot ${isMajorSection ? 'major' : 'sub'}`} />
        <p className={`item-title ${isMajorSection ? 'major' : 'sub'}`}>{data.title}</p>
      </div>

      {data.sceenshotList && (
        <Swiper
          className={data.isWebProject ? 'web-swiper' : 'mobile-swiper'}
          direction={'horizontal'}
          slidesPerView={data.isWebProject ? 1.1 : 1.5}
          spaceBetween={0}
          mousewheel={true}
          pagination={{
            clickable: true,
          }}
          modules={[Pagination]}
          scrollbar={{ draggable: true }}
          breakpoints={
            data.isWebProject
              ? {
                  720: { slidesPerView: 1.2 },
                  1080: { slidesPerView: 1.8 },
                  1440: { slidesPerView: 2.2 },
                  1800: { slidesPerView: 2.8 },
                  2160: { slidesPerView: 3.2 },
                }
              : {
                  720: { slidesPerView: 2.5 },
                  1080: { slidesPerView: 3.5 },
                  1440: { slidesPerView: 4.5 },
                  1800: { slidesPerView: 5.5 },
                  2160: { slidesPerView: 6.5 },
                }
          }>
          {data.sceenshotList?.map((slideItem, slideIndex) => {
            return (
              <SwiperSlide key={slideIndex}>
                <div className={data.isWebProject ? 'mac-window' : 'mobile-frame'}>
                  <img
                    src={slideItem}
                    onClick={() => openImageViewer(slideIndex, data.sceenshotList!)}
                    alt="Screenshot"
                  />
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      )}

      {data.mobileSceenshotList && (
        <Swiper
          direction={'horizontal'}
          slidesPerView={2.5}
          spaceBetween={0}
          mousewheel={true}
          pagination={{
            clickable: true,
          }}
          modules={[Pagination]}
          scrollbar={{ draggable: true }}
          style={{ marginTop: '20px' }}
          breakpoints={{
            720: { slidesPerView: 4.5 },
            1080: { slidesPerView: 6.5 },
            1440: { slidesPerView: 8.5 },
            1800: { slidesPerView: 10.5 },
            2160: { slidesPerView: 12.5 },
          }}>
          {data.mobileSceenshotList?.map((slideItem, slideIndex) => {
            return (
              <SwiperSlide key={slideIndex}>
                <div className="mobile-frame">
                  <img
                    src={slideItem}
                    onClick={() => openImageViewer(slideIndex, data.mobileSceenshotList!)}
                    alt="Mobile Screenshot"
                  />
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      )}

      {isViewerOpen && viewerImages.length > 0 && (
        <ImageViewer
          src={viewerImages}
          currentIndex={currentImage}
          disableScroll={false}
          closeOnClickOutside={true}
          onClose={closeImageViewer}
        />
      )}

      <div className={'item-desc-container'}>
        <p className={'item-desc-title'}>{data.descTitle}</p>
        <p className={'item-desc'}>
          {data.descList.map((descItem, descIndex) => {
            return (
              <>
                {descItem}
                <br />
              </>
            );
          })}
        </p>

        {data.playStoreUrl && (
          <p className={'item-desc'}>
            {'- Google Play Store'}
            <br />
            <a
              className={'item-link'}
              href={data.playStoreUrl}
              target={'_blank'}>
              {data.playStoreUrl}
            </a>
          </p>
        )}

        {data.appStoreUrl && (
          <p className={'item-desc'}>
            {'- AppStore'}
            <br />
            <a
              className={'item-link'}
              href={data.appStoreUrl}
              target={'_blank'}>
              {data.appStoreUrl}
            </a>
          </p>
        )}

        {data.webUrl && (
          <p className={'item-desc'}>
            {'- Web'}
            <br />
            {Array.isArray(data.webUrl) ? (
              data.webUrl.map((url, index) => (
                <React.Fragment key={index}>
                  <a
                    className={'item-link'}
                    href={url}
                    target={'_blank'}
                    rel={'noreferrer'}>
                    {url}
                  </a>
                  <br />
                </React.Fragment>
              ))
            ) : (
              <a
                className={'item-link'}
                href={data.webUrl}
                target={'_blank'}
                rel={'noreferrer'}>
                {data.webUrl}
              </a>
            )}
          </p>
        )}
      </div>
    </div>
  );
};

export default Item;
